/**
 * RatesGrid — «Ставки» rate grid (V1 screens_EO/322 → V4). EO owns the grid
 * (A16); until the EO role ships it is surfaced from the OO ₽-section (editable
 * for OO; pass readOnly to mirror it elsewhere).
 *
 * Sections: global rates (Админ зала ₽/ч, Ставка за отмену) → «Ставки ОС»
 * (fix + percentages) → operator × quest matrix (sticky name column, transpose).
 * Empty matrix cell = no rate = operator does not lead that quest (skill-gate;
 * the «нет ставки» marker the OO board reads, B.12-R8). All edits autosave.
 */
import { useState } from "react";
import { useRates, type OsRates } from "../../hooks/useRates";
import { Transpose } from "./finIcons";
import { Diam, Xmark } from "./v8icons";
import css from "./rates.module.css";

export function RatesGrid({ readOnly = false }: { readOnly?: boolean }) {
  const r = useRates(readOnly);
  const [transposed, setTransposed] = useState(false);

  if (r.loading) {
    return <div className={css.grid}><div className={css.skel} /><div className={css.skel} /><div className={css.skelTall} /></div>;
  }
  if (r.error) {
    return <div className={css.empty}>{r.error}<br /><button type="button" className={css.retry} onClick={r.reload}>Повторить</button></div>;
  }

  const rows = transposed ? r.quests.map((q) => ({ id: q.id, label: q.shortName })) : r.operators.map((o) => ({ id: o.id, label: o.name }));
  const cols = transposed ? r.operators.map((o) => ({ id: o.id, label: o.name })) : r.quests.map((q) => ({ id: q.id, label: q.shortName }));
  const rateAt = (rowId: string, colId: string) => {
    const [opId, qId] = transposed ? [colId, rowId] : [rowId, colId];
    return r.matrix[opId]?.[qId] ?? 0;
  };
  const commit = (rowId: string, colId: string, val: number) => {
    const [opId, qId] = transposed ? [colId, rowId] : [rowId, colId];
    void r.saveCell(opId, qId, val);
  };

  return (
    <div className={css.grid}>
      {readOnly && <div className={css.hint}>Ставки задаёт EO — здесь только просмотр</div>}

      {/* global rates */}
      <div className={css.globals}>
        <div className={css.gRow}>
          <span className={css.gIcon}><Diam aria-hidden="true" /></span>
          <span className={css.gLabel}>АДМИН ЗАЛА</span>
          <RateField value={r.globals.hall_admin_hourly_rate} unit="₽/ч" readOnly={readOnly} onCommit={(v) => r.saveGlobal("hall_admin_hourly_rate", v)} />
        </div>
        <div className={css.gRow}>
          <span className={`${css.gIcon} ${css.gIconX}`}><Xmark aria-hidden="true" /></span>
          <span className={css.gLabel}>СТАВКА ЗА ОТМЕНУ</span>
          <RateField value={r.globals.cancel_rate} unit="₽" readOnly={readOnly} onCommit={(v) => r.saveGlobal("cancel_rate", v)} />
        </div>
      </div>
      {!readOnly && <div className={css.warn}>Изменение ставок применяется только к будущим играм</div>}

      {/* «Ставки ОС» */}
      <div className={css.osCard}>
        <div className={css.osTitle}>Ставки ОС</div>
        <OsField label="Фикс за игру" unit="₽" value={r.os.fix_per_game} readOnly={readOnly} onCommit={(v) => r.saveOs({ ...r.os, fix_per_game: v })} />
        <OsPct label="% допродаж" k="pct_upsell" os={r.os} readOnly={readOnly} save={r.saveOs} />
        <OsPct label="% чайная" k="pct_tea" os={r.os} readOnly={readOnly} save={r.saveOs} />
        <OsPct label="% МК" k="pct_mc" os={r.os} readOnly={readOnly} save={r.saveOs} />
        <OsPct label="% доп. актёр" k="pct_actor" os={r.os} readOnly={readOnly} save={r.saveOs} />
      </div>

      {/* operator × quest matrix */}
      <div className={css.matrixHead}>
        <span className={css.matrixTitle}>Ставки операторов</span>
        <button type="button" className={css.transposeBtn} onClick={() => setTransposed((t) => !t)}>
          <Transpose aria-hidden="true" /> {transposed ? "ИМЕНА СВЕРХУ" : "КВЕСТЫ СВЕРХУ"}
        </button>
      </div>
      {rows.length === 0 || cols.length === 0 ? (
        <div className={css.empty}>Нет {transposed ? "квестов" : "операторов"} для сетки</div>
      ) : (
        <div className={css.scroller}>
          <table className={css.table}>
            <thead>
              <tr>
                <th className={css.corner}>{transposed ? "Квест" : "Имя"}</th>
                {cols.map((c) => <th key={c.id} className={css.colHead} title={c.label}>{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <th className={css.nameCell} title={row.label}>{row.label}</th>
                  {cols.map((c) => (
                    <td key={c.id} className={css.cell}>
                      <Cell value={rateAt(row.id, c.id)} readOnly={readOnly} onCommit={(v) => commit(row.id, c.id, v)} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className={css.gridFoot} />
    </div>
  );
}

/* ── editable matrix cell (numeric, autosave on blur/Enter) ───────────────── */
function Cell({ value, readOnly, onCommit }: { value: number; readOnly: boolean; onCommit: (v: number) => void }) {
  const [v, setV] = useState(value > 0 ? String(value) : "");
  if (readOnly) {
    return value > 0 ? <span className={css.cellRo}>{value}</span> : <span className={css.cellEmpty}>—</span>;
  }
  return (
    <input
      className={`${css.cellInput} ${v ? css.cellSet : ""}`}
      inputMode="numeric"
      value={v}
      placeholder="—"
      onChange={(e) => setV(e.target.value.replace(/[^\d]/g, ""))}
      onBlur={() => { const n = Number(v) || 0; if (n !== value) onCommit(n); }}
      onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
    />
  );
}

/* ── global / ОС rate field ──────────────────────────────────────────────── */
function RateField({ value, unit, readOnly, onCommit }: { value: number; unit: string; readOnly: boolean; onCommit: (v: number) => void }) {
  const [v, setV] = useState(value > 0 ? String(value) : "");
  return (
    <span className={css.field}>
      {readOnly
        ? <span className={css.fieldRo}>{value > 0 ? value : "—"}</span>
        : <input className={css.fieldInput} inputMode="numeric" value={v} placeholder="—"
            onChange={(e) => setV(e.target.value.replace(/[^\d.]/g, ""))}
            onBlur={() => { const n = Number(v) || 0; if (n !== value) onCommit(n); }}
            onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }} />}
      <span className={css.unit}>{unit}</span>
    </span>
  );
}

function OsField({ label, unit, value, readOnly, onCommit }: { label: string; unit: string; value: number; readOnly: boolean; onCommit: (v: number) => void }) {
  return (
    <div className={css.osRow}>
      <span className={css.osLabel}>{label}</span>
      <RateField value={value} unit={unit} readOnly={readOnly} onCommit={onCommit} />
    </div>
  );
}

function OsPct({ label, k, os, readOnly, save }: { label: string; k: keyof OsRates; os: OsRates; readOnly: boolean; save: (next: OsRates) => Promise<boolean> }) {
  return <OsField label={label} unit="%" value={os[k]} readOnly={readOnly} onCommit={(v) => save({ ...os, [k]: v })} />;
}
